import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import {
  useZepApiClient,
  UseZepApiMutationOptions,
  UseZepApiQueryOptions,
  useZepetofulContents,
} from '@zep/apis';

import { AccountQueryKeys, ManageQueryKeys, ZepetofulEntries } from '../consts';
import {
  PrivacyJson,
  ProfileForm,
  TermsJson,
  User,
} from '../types/AccountsTypes';

/**
 * 유저 정보 저장
 * @param options
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useUserProfileMutation = (
  options?: UseZepApiMutationOptions<
    { status: number; player: ProfileForm },
    ProfileForm
  >,
) => {
  const client = useZepApiClient();

  return useMutation(
    [AccountQueryKeys.PROFILE_UPDATE],
    async profileData => {
      const { data } = await client.put('/v2/me/profile', profileData);

      return data;
    },
    options,
  );
};

/**
 * 소셜 로그인 처리
 * @param options
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useSnsLoginMutation = (
  options?: UseZepApiMutationOptions<
    {
      status: number;
      player: User;
      loginToken: string;
    },
    {
      sns_token: string;
      sns_type: string;
      email: string;
    }
  >,
) => {
  const client = useZepApiClient();

  return useMutation(
    [AccountQueryKeys.SNS_LOGIN],
    async requestData => {
      const { data } = await client.post('/v2/signin/sns', requestData);

      return data;
    },
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useAgreeTerms = (
  options?: UseMutationOptions<
    { status: number; player: User },
    AxiosError,
    { token: string }
  >,
) => {
  const client = useZepApiClient();
  return useMutation(
    ['account-agree-terms'],
    async ({ token }: { token: string }) => {
      const { data } = await client.post(
        '/v2/signin/agree-terms-and-privacy-policy',
        {},
        {
          headers: {
            'X-Token': token,
          },
        },
      );
      return data;
    },
    options,
  );
};

type UploadAppPayload = {
  file: File;
  icon: File;
  name: string;
  description: string;
  type: number;
  url?: string;
  isUpdated: boolean;
  appHashId?: string;
  isDeletedIcon?: boolean;
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useUploadApps = () => {
  const queryClient = useQueryClient();
  const client = useZepApiClient();

  return useMutation<
    { data: { hashId: string } },
    AxiosError,
    UploadAppPayload,
    string[]
  >(
    ['my-apps-create'],
    (data: UploadAppPayload) => {
      if (data.isUpdated) {
        return client.putForm('/v2/me/apps', data, {
          timeout: 100 * 1000,
        });
      }

      return client.postForm('/v2/me/apps', data, {
        timeout: 100 * 1000,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ManageQueryKeys.MY_APP_LIST_GET]);
      },
    },
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useDeleteApp = () => {
  const queryClient = useQueryClient();
  const client = useZepApiClient();

  return useMutation(
    ['my-apps-delete'],
    (appHashId: string) => {
      return client.delete('/v2/me/apps', {
        params: {
          appHashId,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ManageQueryKeys.MY_APP_LIST_GET]);
      },
    },
  );
};

/**
 * @desc 특정 앱의 정보를 가져오기
 * @alias 'API_V2_APPS_DETAILS'
 * @return Query States
 * */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export function useAppDetail(appHashId: string) {
  const client = useZepApiClient();
  return useQuery(['appDetail', appHashId], async () => {
    const { data: appDetail } = await client.get<AppDetailResponse>(
      '/v2/me/app-details',
      {
        params: {
          appHashId,
        },
      },
    );
    return appDetail.data;
  });
}

export type ZepApp = {
  description: string;
  finalIconUrl: string;
  hashId: string;
  id: number;
  name: string;
  /** 젭 스크립트 local path 가 담겨져잇다 */
  url?: string;
  type: ZepAppType;
  createdAt: string;
  updatedAt: string;
};

export enum ZepAppType {
  Normal = 1,
  MiniGame = 2,
  Sidebar = 3,
}

export interface AppDetailResponse {
  data: ZepApp;
}

export function usePrivacy(options?: UseZepApiQueryOptions<PrivacyJson>) {
  return useZepetofulContents(ZepetofulEntries.PRIVACY, options);
}

export function useTerms(options?: UseZepApiQueryOptions<TermsJson>) {
  return useZepetofulContents(ZepetofulEntries.TERMS, options);
}
